import { debounce } from 'lodash';
import type { EditorPlugin } from '../../types/aha-editor';
import type AhaEditorV2Controller from '../../controllers/AhaEditorV2Controller';
import findScrollContainer from '../../util/findScrollContainer';

/**
 * This plugin will automatically scroll the editor's scroll container or window to keep a specified
 * margin around the cursor. This is useful to prevent the top toolbar or selection toolbar from covering
 * the cursor when it approaches the edge of the scroll container. However, a custom margin is needed
 * to support places in the app that need more margin, e.g. in drawers where we need to allow for the tabs
 * to be visible as well.
 *
 * The options allow you to specify the margin(s) to maintain on each side of the editor.
 *
 * Example usage:
 *
 *   // This will maintain a margin of 50px on the top, and 100px on the bottom.
 *   plugins.push(SmoothScrollingPlugin({ top: 50, bottom: 100 }))
 */

interface SmoothScrollingPluginOptions {
  behavior?: ScrollBehavior;
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

function getNearestElement(node: Node): HTMLElement | undefined {
  if (node instanceof HTMLElement) {
    return node;
  } else if ('parentElement' in node && node.parentElement) {
    return node.parentElement;
  }
}

const SmoothScrollingPlugin = (
  options: SmoothScrollingPluginOptions
): EditorPlugin => {
  const {
    behavior = 'smooth',
    top = 0,
    bottom = 0,
    left = 0,
    right = 0,
  } = options;

  let controller: AhaEditorV2Controller;
  const selection = window.getSelection()!;

  // We debounce this, since some selection changes trigger two events
  const handler = debounce(() => {
    if (selection?.rangeCount < 1) {
      return;
    }

    // Don't run if the selection is not collapsed
    if (!selection.isCollapsed) {
      return;
    }

    // We need an editor container to work
    const { editorContainer } = controller;
    if (!editorContainer) {
      return;
    }

    // Assume single selection range
    const range = selection.getRangeAt(0);
    if (!range) {
      return;
    }

    // Selection range must be inside the editor container, otherwise bail out
    if (!editorContainer.contains(range.startContainer)) {
      return;
    }

    // We also don't want to scroll unless the cursor/selection is in the editor content
    const parentElement = getNearestElement(range.startContainer);
    if (!parentElement?.closest('.aha-editor-v2__body')) {
      return;
    }

    const selectionRect = range.getBoundingClientRect();
    if (
      !selectionRect ||
      selectionRect.width === 0 ||
      selectionRect.height === 0
    ) {
      return;
    }

    let offset: { left: number; right: number; top: number; bottom: number };

    // Is the editor inside an `overflow` scroll container?
    const scrollContainer = findScrollContainer(editorContainer);
    if (scrollContainer) {
      const scrollContainerRect = scrollContainer.getBoundingClientRect();

      offset = {
        top: selectionRect.top - scrollContainerRect.top,
        bottom: scrollContainerRect.bottom - selectionRect.bottom,
        left: selectionRect.left - scrollContainerRect.left,
        right: scrollContainerRect.right - selectionRect.right,
      };
    } else {
      offset = {
        top: selectionRect.top,
        bottom: window.innerHeight - selectionRect.bottom,
        left: selectionRect.left,
        right: window.innerWidth - selectionRect.right,
      };
    }

    let scroll = false;
    const scrollOptions: ScrollToOptions = {
      behavior,
    };

    if (offset.top < top) {
      scroll = true;
      scrollOptions.top = offset.top - top;
    } else if (offset.bottom < bottom) {
      scroll = true;
      scrollOptions.top = bottom - offset.bottom;
    }

    if (offset.left < left) {
      scroll = true;
      scrollOptions.left = offset.left - left;
    } else if (offset.right < right) {
      scroll = true;
      scrollOptions.left = right - offset.right;
    }

    if (scroll) {
      const scroller = scrollContainer || window;
      scroller.scrollBy(scrollOptions);
    }
  }, 10);

  return {
    name: 'Smooth scrolling plugin',
    handlers: {
      onInit(_ahaEditor) {
        controller = _ahaEditor.controller;

        document.addEventListener('selectionchange', handler);
      },

      onDestroy() {
        document.removeEventListener('selectionchange', handler);
      },
    },
  };
};

export default SmoothScrollingPlugin;
